import { ampli } from '@/ampli';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { useSources } from '@/hooks/useSources';

import { liquiditySourcesEventHandlers, useLiquiditySourcesStore } from '@/store/liquiditySources';

import { breakpoints } from '@/styles/util';

import { Modal } from '@/ui/Modal';
import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';

import { TokenResult } from '@/utils/0x/token-registry.types';

import { Header } from './Header';
import { LiquiditySources, Main } from './Menus';
import { NoLiquiditySourceSelectedModal } from './NoLiquiditySourceSelectedModal';

/**
 * Keys for all the Settings menus.
 */
export const enum MenuId {
  LIQUIDITY_SOURCES,
  MAIN,
}

/**
 * A data structure that specifies the properties of a settings menu.
 */
export type MenuSettings = {
  /** Menu title displayed in the header. */
  title: string;
  /** Menu description for accessibility. */
  description: string;
  /** The JSX component to render. */
  component: React.ReactNode;
};

/**
 * A map with all the menu settings.
 */
type MenuSettingsMap = {
  /** Key is the menu ID. */
  [key in MenuId]: MenuSettings;
};

interface GlobalMarketSettingsModalProps {
  /** Buy token */
  buyToken: TokenResult | undefined;
  /** Buy token chain ID. */
  buyTokenChainId: number | undefined;
  /** ID of the current network. */
  chainId: number;
  /** Sell token */
  sellToken: TokenResult | undefined;
  /** The modals' visibility state. */
  shouldShow: boolean;
  /** Action to update modal visibility. */
  setShouldShow: Dispatch<SetStateAction<boolean>>;
}

/**
 * A modal dialog to configure a users Global Market trade settings.
 */
export const GlobalMarketSettingsModal = ({
  buyToken,
  buyTokenChainId,
  chainId,
  sellToken,
  shouldShow,
  setShouldShow,
}: GlobalMarketSettingsModalProps) => {
  const isCrossChain = buyTokenChainId != null && buyTokenChainId !== chainId;
  // store
  const { addLiquiditySourcesForChain, setEnabledLiquiditySources } = useLiquiditySourcesStore(
    liquiditySourcesEventHandlers,
  );
  const isMobile = useMedia(breakpoints.mobile, false);

  // null means that no changes have been staged
  const [stagedLSIds, setStagedLSIds] = useState<string[] | null>(null);

  // state
  const [currentMenuId, setCurrentMenuId] = useState<MenuId>(MenuId.MAIN);
  const {
    total,
    selected,
    sources,
    isLoading: sourcesLoading,
  } = useSources(chainId, !isCrossChain);
  const [shouldShowNoLSModal, setShouldShowNoLSModal] = useState(false);

  // effects
  useEffect(() => {
    if (!isCrossChain && !sourcesLoading) {
      addLiquiditySourcesForChain(chainId, sources, total, selected);
    }
  }, [
    addLiquiditySourcesForChain,
    chainId,
    isCrossChain,
    selected,
    sources,
    sourcesLoading,
    total,
  ]);

  const commitLiquiditySources = () => {
    if (stagedLSIds === null) return;
    setEnabledLiquiditySources(chainId, stagedLSIds);

    const sourceDisplayNames = stagedLSIds.map((id) => sources[id]?.displayName ?? id);
    ampli.liquiditySourcesSelected({ liquidity_sources: sourceDisplayNames });

    setStagedLSIds(null);
  };

  // event handlers
  const handleCurrentMenuId = (newMenuId: MenuId) => {
    if (currentMenuId === MenuId.LIQUIDITY_SOURCES) {
      // We need at least one liquidity source
      if (Array.isArray(stagedLSIds) && stagedLSIds.length === 0) {
        setShouldShowNoLSModal(true);
        return;
      }
      commitLiquiditySources();
    }
    setCurrentMenuId(newMenuId);
  };
  const handleOpenChange = (value: boolean) => {
    // Nothing to do if the modal is being opened.
    if (value) return;

    // If the modal is being closed
    switch (currentMenuId) {
      case MenuId.MAIN:
        // Otherwise, we close the modal.
        setShouldShow(false);
        break;

      case MenuId.LIQUIDITY_SOURCES:
        // We need at least one liquidity source
        if (Array.isArray(stagedLSIds) && stagedLSIds.length === 0) {
          setShouldShowNoLSModal(true);

          break;
        }

        commitLiquiditySources();
        setShouldShow(false);
        setCurrentMenuId(MenuId.MAIN);
        break;

      default:
        setCurrentMenuId(MenuId.MAIN);
        setShouldShow(false);
        break;
    }
  };

  const menuSettingsMap: MenuSettingsMap = {
    [MenuId.MAIN]: {
      title: isCrossChain ? 'Cross-chain settings' : 'Market settings',
      description: 'Change your trade settings.',
      component: (
        <Main
          buyToken={buyToken}
          chainId={chainId}
          isCrossChain={isCrossChain}
          sellToken={sellToken}
          setCurrentMenu={setCurrentMenuId}
        />
      ),
    },
    [MenuId.LIQUIDITY_SOURCES]: {
      title: 'Liquidity Sources',
      description: 'Select exchanges to source liquidity from.',
      component: (
        <LiquiditySources
          chainId={chainId}
          onSourcesSelected={setStagedLSIds}
          stagedSources={stagedLSIds}
        />
      ),
    },
  };

  const currentMenu = menuSettingsMap[currentMenuId];

  return (
    <>
      {isMobile ? (
        <BottomPanelModal
          isOpen={shouldShow}
          onClose={() => handleOpenChange(false)}
          onOpenChange={handleOpenChange}
          variant="LARGE"
          allowGestures={isMobile}
        >
          <Header
            chainId={chainId}
            destinationChainId={isCrossChain ? buyTokenChainId : undefined}
            currentMenuId={currentMenuId}
            currentMenu={currentMenu}
            setCurrentMenuId={handleCurrentMenuId}
          />
          {currentMenu.component}
        </BottomPanelModal>
      ) : (
        <Modal
          isOpen={shouldShow}
          onClose={() => handleOpenChange(false)}
          onOpenChange={handleOpenChange}
          variant="LARGE"
          className="shadow-30 grid grid-flow-row"
        >
          <Header
            chainId={chainId}
            destinationChainId={isCrossChain ? buyTokenChainId : undefined}
            currentMenuId={currentMenuId}
            currentMenu={currentMenu}
            setCurrentMenuId={handleCurrentMenuId}
          />
          {currentMenu.component}
        </Modal>
      )}

      <NoLiquiditySourceSelectedModal
        isOpen={shouldShowNoLSModal}
        onClose={() => setShouldShowNoLSModal(false)}
      />
    </>
  );
};
